import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteFooter from "@/components/pivotbox/SiteFooter";
import SiteHeader from "@/components/pivotbox/SiteHeader";
import StageBreadcrumb from "@/components/pivotbox/StageBreadcrumb";
import UnlockDiagnosticCTA from "@/components/pivotbox/UnlockDiagnosticCTA";
import "../S1Diagnostic.css";
import "./SDiagnosticExtras.css";

/*
 * SDiagBase — shared layout for all stage landing pages (S0, S2.1, S2.2, S2.3).
 * Reuses S1Diagnostic.css (.s1d-*) since the visual language is identical to S1.
 *
 * Each calling page supplies the per-stage JSX blocks + a config object.
 */
export default function SDiagBase({
  stage,                // "S0" | "S2.1" | "S2.2" | "S2.3"
  isFree = false,
  pageUrl,
  pageTitle,
  testId,
  ctaTo = "/diagnostic", // override per-stage if CTA should route elsewhere (e.g. S0 -> /#founder-signal-test)
  hero,                 // { eyebrowTrail, headlineLead, headlineItalic, sub, ctaLabel, priceLabel }
  intro,                // { label, body, bullets: [{strong, body}], methodologyCols: [{label, body}] }
  why,                  // { eyebrow, headlineLead, headlineItalic, paragraphs: [], callout }
  measures,             // { eyebrow, headline, intro, cards: <JSX> }   (cards rendered by caller)
  receive,              // { eyebrow, eyebrowAccent, callout, heading, intro, bullets, report: <JSX> }
  helps,                // { eyebrow, heading, intro, cards: [{ num, title, body }] }
  framework,            // [{ stage, name, q, badge, isFree, isCurrent, isCompleted, isSub }]
  finalCta,             // { heading, sub, price: { strike, current } | null, ctaLabel }
}) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="s1d-page" data-testid={testId}>
      <SiteHeader testIdPrefix={testId} />

      {/* HERO */}
      <section className="s1d-hero" data-testid={`${testId}-hero`}>
        <div className="s1d-container">
          <div className="s1d-hero__eyebrow">
            <span className="s1d-hero__eyebrow-line" />
            <span>{hero.eyebrowTrail}</span>
          </div>
          <h1 className="s1d-hero__headline" data-testid={`${testId}-hero-headline`}>
            {hero.headlineLead}<br /><em>{hero.headlineItalic}</em>
          </h1>
          <p className="s1d-hero__sub">{hero.sub}</p>
          <div className="s1d-hero__actions">
            {ctaTo === "/diagnostic" ? (
              <UnlockDiagnosticCTA
                className="s1d-btn-primary"
                buttonLabel={`${hero.ctaLabel} \u2192`}
                testId={`${testId}-hero-cta`}
              />
            ) : ctaTo.startsWith("/#") ? (
              <a href={ctaTo} className="s1d-btn-primary" data-testid={`${testId}-hero-cta`}>
                {hero.ctaLabel} &rarr;
              </a>
            ) : (
              <Link to={ctaTo} className="s1d-btn-primary" data-testid={`${testId}-hero-cta`}>
                {hero.ctaLabel} &rarr;
              </Link>
            )}
            <span className="s1d-hero__price">{hero.priceLabel}</span>
          </div>

          {/* Stage progression breadcrumb (S0 → S4) */}
          <StageBreadcrumb currentStage={stage} testId={`${testId}-breadcrumb`} />

          <hr className="s1d-hero__divider" />
        </div>
      </section>

      {/* INTRO BULLETS — paid stages have "You have completed Sx. Here's what comes next." */}
      {intro && (
        <section className="s1d-section" data-testid={`${testId}-intro`}>
          <div className="s1d-container">
            <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">{intro.label}</span></div>
            {intro.body && <p className="s1d-intro">{intro.body}</p>}
            {intro.bullets && (
              <div className="sd-bullet-grid" data-testid={`${testId}-intro-bullets`}>
                {intro.bullets.map((b, i) => (
                  <div className="sd-bullet" key={i}>
                    <p className="sd-bullet__body"><strong>{b.strong}</strong> {b.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* WHY */}
      {why && (
        <section className="s1d-section" data-testid={`${testId}-why`}>
          <div className="s1d-container">
            <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">{why.eyebrow}</span></div>
            <h2 className="s1d-heading">{why.headlineLead}<br /><em style={{ color: "#2F7D57", fontStyle: "italic" }}>{why.headlineItalic}</em></h2>
            <div className="s1d-why-layout">
              <div className="s1d-why-body">
                {why.paragraphs.map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}
              </div>
              {why.callout && (
                <div className="s1d-why-callout">
                  <span className="s1d-why-callout__icon">&darr;</span>
                  <p>{why.callout}</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* INSIDE THE DIAGNOSTIC — methodology dark band */}
      {intro?.methodologyCols && (
        <section className="s1d-method" data-testid={`${testId}-method`}>
          <div className="s1d-container">
            <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">Inside the diagnostic</span></div>
            <h2 className="s1d-heading">How {stage} is built, and how it is scored.</h2>
            <div className="s1d-method__grid">
              {intro.methodologyCols.map((c, i) => (
                <div className="s1d-method__col" key={i}>
                  <div className="s1d-method__label">{c.label}</div>
                  <p dangerouslySetInnerHTML={{ __html: c.body }} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHAT IT MEASURES — caller-supplied JSX (variable card count/layout) */}
      {measures && (
        <section className="s1d-section" data-testid={`${testId}-measures`}>
          <div className="s1d-container">
            <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">{measures.eyebrow}</span></div>
            <h2 className="s1d-heading">{measures.headline}</h2>
            {measures.intro && <p className="s1d-intro">{measures.intro}</p>}
            {measures.cards}
          </div>
        </section>
      )}

      {/* HOW IT HELPS */}
      {helps && (
        <section className="s1d-section" data-testid={`${testId}-helps`}>
          <div className="s1d-container">
            <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">{helps.eyebrow}</span></div>
            <h2 className="s1d-heading">{helps.heading}</h2>
            {helps.intro && <p className="s1d-intro">{helps.intro}</p>}
            <div className="s1d-helps-grid">
              {helps.cards.map((h) => (
                <div className="s1d-helps-card" key={h.num}>
                  <span className="s1d-helps-num">{h.num}</span>
                  <h3 className="s1d-helps-title">{h.title}</h3>
                  <p className="s1d-helps-desc">{h.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHAT YOU RECEIVE */}
      {receive && (
        <section className="s1d-section s1d-receive" data-testid={`${testId}-receive`}>
          <div className="s1d-container">
            <div className={`s1d-label ${receive.eyebrowAccent ? `s1d-label--${receive.eyebrowAccent}` : ""}`}>
              <span className="s1d-label__line" /><span className="s1d-label__text">{receive.eyebrow}</span>
            </div>
            {receive.callout && <p className="s1d-receive__callout" dangerouslySetInnerHTML={{ __html: receive.callout }} />}
            <div className="s1d-receive__grid">
              <div className="s1d-receive__body">
                <h2 className="s1d-heading">{receive.heading}</h2>
                <p className="s1d-intro">{receive.intro}</p>
                <ul className="s1d-receive__list">
                  {receive.bullets.map((b, i) => (
                    <li key={i}>
                      <span className="s1d-receive__check">&#10003;</span>
                      <div><strong>{b.strong}</strong> {b.body}</div>
                    </li>
                  ))}
                </ul>
              </div>
              {receive.report}
            </div>
          </div>
        </section>
      )}

      {/* WHERE IT FITS — framework table */}
      {framework && (
        <section className="s1d-section s1d-section--dark" data-testid={`${testId}-framework`}>
          <div className="s1d-container">
            <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">Where {stage} fits</span></div>
            <h2 className="s1d-heading s1d-heading--white">Pivotbox is a 5S diagnostic framework.<br />Every stage builds on the last.</h2>
            <div className="s1d-fw-table">
              <div className="s1d-fw-row s1d-fw-row--header">
                <div className="s1d-fw-col-stage">Stage</div>
                <div className="s1d-fw-col-name">Assessment</div>
                <div className="s1d-fw-col-q">The question it answers</div>
              </div>
              {framework.map((row) => {
                const cls = [
                  row.isCurrent ? "s1d-fw-row--current" : "",
                  row.isFree ? "s1d-fw-row--free" : "",
                  row.isSub ? "s1d-fw-row--sub" : "",
                  row.isCompleted ? "s1d-fw-row--done" : "",
                  row.isFuture ? "s1d-fw-row--future" : "",
                ].filter(Boolean).join(" ");
                return (
                  <div className={`s1d-fw-row ${cls}`} key={row.stage}>
                    <div className="s1d-fw-col-stage"><span className={`s1d-fw-stage ${row.isCurrent ? "s1d-fw-stage--current" : ""}`}>{row.stage}</span></div>
                    <div className="s1d-fw-col-name"><strong>{row.name}</strong>{row.badge && <span className={`s1d-fw-badge ${row.isCurrent ? "s1d-fw-badge--current" : ""}`}>{row.badge}</span>}</div>
                    <div className="s1d-fw-col-q">{row.q}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* REFUND — paid only */}
      {!isFree && (
        <section className="s1d-refund" data-testid={`${testId}-refund`}>
          <div className="s1d-container">
            <div className="s1d-refund__card">
              <div className="s1d-refund__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" /><path d="m9 12 2 2 4-4" /></svg>
              </div>
              <div className="s1d-refund__body">
                <h3 className="s1d-refund__heading">If it does not deliver, it does not cost.</h3>
                <p className="s1d-refund__sub">Take the full Pivotbox diagnostic. If it does not give you the clarity we promise, we will refund you in full. No questions, no forms, no friction.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="s1d-cta-section" data-testid={`${testId}-final-cta`} id="start">
        <div className="s1d-container s1d-cta-inner">
          <div className="s1d-cta-copy">
            <span className="s1d-cta-logo-wrap">
              <img src="/pivotbox-p-logo.png" alt="Pivotbox" className="s1d-cta-logo" />
              <span className="s1d-cta-logo-text">Pivotbox</span>
            </span>
            <h2 className="s1d-cta-heading">{finalCta.heading}</h2>
            <p className="s1d-cta-sub">{finalCta.sub}</p>
          </div>
          <div className="s1d-cta-right">
            <div className="s1d-cta-offer">
              <span className="s1d-cta-offer__label">{isFree ? "Always free" : "Complete diagnostic"}</span>
              <div className="s1d-cta-offer__prices">
                {!isFree && <span className="s1d-cta-offer__strike">&#8377;{finalCta.price.strike}</span>}
                <span className="s1d-cta-offer__amount">{isFree ? <>&#8377;0</> : <>&#8377;{finalCta.price.current}</>}</span>
              </div>
            </div>
            {ctaTo === "/diagnostic" ? (
              <UnlockDiagnosticCTA
                className="s1d-btn-white"
                buttonLabel={`${finalCta.ctaLabel} \u2192`}
                testId={`${testId}-final-cta-btn`}
              />
            ) : ctaTo.startsWith("/#") ? (
              <a href={ctaTo} className="s1d-btn-white" data-testid={`${testId}-final-cta-btn`}>
                {finalCta.ctaLabel} &rarr;
              </a>
            ) : (
              <Link to={ctaTo} className="s1d-btn-white" data-testid={`${testId}-final-cta-btn`}>
                {finalCta.ctaLabel} &rarr;
              </Link>
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
